import { useEffect, useRef, useState } from 'react';
import { parseMeasurement, formatMeasurement } from '../../utils/fraction';

/**
 * Measurement entry field with tailoring slash shorthand.
 *
 *   40/  -> 40 ¼      40//  -> 40 ½      40///  -> 40 ¾
 *
 * Decimals (40.5) and explicit fractions (40 1/2) work too. See
 * `utils/fraction.js` for the full grammar.
 *
 * The field keeps its own draft string while focused so the tailor can type
 * freely — reformatting mid-keystroke would fight the numpad. It commits on
 * blur, Tab and Enter; on re-focus it drops back to editable text so backspace
 * behaves normally instead of deleting a glyph.
 *
 * Enter/Tab advance to the next `[data-measurement-input]` in the DOM, so a
 * whole garment can be measured without touching the mouse.
 *
 * @param {number|null} value     committed decimal value
 * @param {function}    onChange  called with (decimal|null) on commit
 */
export default function MeasurementInput({
  value,
  onChange,
  className = '',
  placeholder = 'e.g. 40//',
  disabled,
  ...rest
}) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  // Keep the display in step with the committed value while not being edited
  // (e.g. the row was loaded, reset, or auto-filled from the last order).
  useEffect(() => {
    if (!focused) setDraft(formatMeasurement(value));
  }, [value, focused]);

  const commit = () => {
    const parsed = parseMeasurement(draft);
    setDraft(formatMeasurement(parsed));
    // Only notify when the value actually moved, so a blur on an untouched
    // field doesn't mark the form dirty.
    if (parsed !== (value ?? null)) onChange?.(parsed);
    return parsed;
  };

  const focusNext = (backwards = false) => {
    const fields = Array.from(document.querySelectorAll('[data-measurement-input]'));
    const i = fields.indexOf(ref.current);
    const next = fields[i + (backwards ? -1 : 1)];
    if (next) {
      next.focus();
      next.select?.();
    } else {
      ref.current?.blur();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Commit and advance without submitting the surrounding form.
      e.preventDefault();
      commit();
      focusNext();
      return;
    }
    if (e.key === 'Tab') {
      // Let the browser move focus; just make sure the value is committed.
      commit();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(formatMeasurement(value));
      ref.current?.blur();
    }
  };

  return (
    <input
      {...rest}
      ref={ref}
      data-measurement-input=""
      className={`input measurement-val ${className}`}
      // `text` rather than `number`: a number input rejects "40//" outright
      // and its spinners are useless here. `decimal` still gets the numpad
      // on mobile, and the slash stays reachable.
      type="text"
      inputMode="decimal"
      autoComplete="off"
      disabled={disabled}
      placeholder={placeholder}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        setFocused(true);
        // Show the raw number while editing — "40 ½" is awkward to backspace.
        setDraft(value == null ? '' : String(value));
        requestAnimationFrame(() => e.target.select());
      }}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onKeyDown={onKeyDown}
      title="Type 40/ for 40¼, 40// for 40½, 40/// for 40¾"
    />
  );
}
